import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PublicationDetailSEO } from '../components/SEO';
import { CheckCircle, ArrowRight, Globe, Link as LinkIcon, AlertCircle, ExternalLink, Image, Share2, Twitter, Linkedin, Facebook } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [publication, setPublication] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublication();
  }, [id]);

  const fetchPublication = async () => {
    try {
      const [pubResponse, packagesResponse] = await Promise.all([
        axios.get(`${API}/publications/${id}`),
        axios.get(`${API}/publications/${id}/packages`)
      ]);
      
      setPublication(pubResponse.data);
      setPackages(packagesResponse.data);
      
      const normalPackage = packagesResponse.data.find(p => p.name === 'Normal');
      if (normalPackage) setSelectedPackage(normalPackage);
    } catch (error) {
      console.error('Error fetching publication:', error);
      toast.error('Failed to load publication details');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = () => {
    if (!selectedPackage) {
      toast.error('Please select a package');
      return;
    }
    navigate('/checkout', { 
      state: { 
        publication, 
        package: selectedPackage 
      } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <p className="font-manrope text-center text-[#737373]">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!publication) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <p className="font-manrope text-center text-[#737373]">Publication not found</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PublicationDetailSEO publication={publication} />
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="mb-8">
          {/* Categories and DA Badge */}
          <div className="flex flex-wrap gap-2 mb-4">
            {(publication.categories || [publication.category]).filter(Boolean).map((cat, idx) => (
              <span key={idx} className="inline-block px-3 py-1 bg-[#f5f5f5] text-[#737373] font-manrope text-xs uppercase tracking-widest">
                {cat}
              </span>
            ))}
            {publication.da && (
              <span className="inline-block px-3 py-1 bg-[#f5f5f5] text-[#D4AF37] font-mono text-xs font-bold">
                DA {publication.da}
              </span>
            )}
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#f5f5f5] text-[#737373] text-xs font-manrope">
              <Globe className="w-3 h-3" />
              {publication.language || 'English'}
            </span>
          </div>
          
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-4 text-[#1a1a1a]">
            {publication.name}
          </h1>
          <p className="font-manrope text-base text-[#737373] mb-4">
            {publication.description}
          </p>
          
          {/* Share Buttons */}
          <div className="flex items-center gap-3 mb-6">
            <span className="font-manrope text-sm text-[#737373]">Share:</span>
            <button
              onClick={() => {
                const shareUrl = `${BACKEND_URL}/api/share/publication/${id}`;
                window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`Get featured on ${publication.name} - BusinessMint PR`)}`, '_blank', 'width=600,height=400');
              }}
              className="p-2 bg-[#1DA1F2] text-white rounded-lg hover:opacity-90 transition-opacity"
              title="Share on Twitter"
            >
              <Twitter className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                const shareUrl = `${BACKEND_URL}/api/share/publication/${id}`;
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
              }}
              className="p-2 bg-[#0A66C2] text-white rounded-lg hover:opacity-90 transition-opacity"
              title="Share on LinkedIn"
            >
              <Linkedin className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                const shareUrl = `${BACKEND_URL}/api/share/publication/${id}`;
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
              }}
              className="p-2 bg-[#1877F2] text-white rounded-lg hover:opacity-90 transition-opacity"
              title="Share on Facebook"
            >
              <Facebook className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast.success('Link copied to clipboard!');
              }}
              className="p-2 bg-[#1a1a1a] text-white rounded-lg hover:opacity-90 transition-opacity"
              title="Copy link"
            >
              <LinkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Quick Info Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {publication.backlink !== false ? (
              <div className="border border-[#1a3d1a] bg-[#0d1f0d] p-4">
                <div className="flex items-center gap-2 text-[#4ade80] mb-1">
                  <LinkIcon className="w-4 h-4" />
                  <span className="font-manrope text-xs uppercase tracking-widest font-semibold">Backlink</span>
                </div>
                <p className="font-manrope text-sm text-[#4ade80]">Dofollow links allowed</p>
              </div>
            ) : (
              <div className="border border-[#e5e5e5] bg-[#fafafa] p-4">
                <div className="flex items-center gap-2 text-[#a3a3a3] mb-1">
                  <LinkIcon className="w-4 h-4" />
                  <span className="font-manrope text-xs uppercase tracking-widest font-semibold">Backlink</span>
                </div>
                <p className="font-manrope text-sm text-[#a3a3a3]">No backlinks</p>
              </div>
            )}

            <div className={`border p-4 ${publication.disclaimer ? 'border-[#3d2a1a] bg-[#1f150d]' : 'border-[#e5e5e5] bg-[#fafafa]'}`}>
              <div className={`flex items-center gap-2 mb-1 ${publication.disclaimer ? 'text-[#fbbf24]' : 'text-[#a3a3a3]'}`}>
                <AlertCircle className="w-4 h-4" />
                <span className="font-manrope text-xs uppercase tracking-widest font-semibold">Disclaimer</span>
              </div>
              <p className={`font-manrope text-sm ${publication.disclaimer ? 'text-[#fbbf24]' : 'text-[#a3a3a3]'}`}>
                {publication.disclaimer ? 'Required' : 'Not required'}
              </p>
            </div>

            {publication.image_resolution && (
              <div className="border border-[#e5e5e5] bg-[#fafafa] p-4">
                <div className="flex items-center gap-2 text-[#737373] mb-1">
                  <Image className="w-4 h-4" />
                  <span className="font-manrope text-xs uppercase tracking-widest font-semibold">Image Res.</span>
                </div>
                <p className="font-manrope text-sm text-[#737373]">{publication.image_resolution}</p>
              </div>
            )}

            {publication.da && (
              <div className="border border-[#e5e5e5] bg-[#fafafa] p-4">
                <div className="flex items-center gap-2 text-[#737373] mb-1">
                  <span className="font-manrope text-xs uppercase tracking-widest font-semibold">Domain Authority</span>
                </div>
                <p className="font-mono text-2xl font-bold text-[#D4AF37]">{publication.da}</p>
              </div>
            )}
          </div>
        </div>
        
        <hr className="border-[#e5e5e5] mb-12" />
        
        <div className="mb-12">
          <h2 className="font-playfair text-2xl font-bold mb-6 text-[#1a1a1a]">Select Package</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.map(pkg => (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg)}
                data-testid={`package-card-${pkg.name.toLowerCase()}`}
                className={`border p-6 text-left transition-all duration-300 ${
                  selectedPackage?.id === pkg.id 
                    ? 'border-[#D4AF37] bg-[#D4AF37] text-[#1a1a1a]' 
                    : 'border-[#e5e5e5] bg-[#fafafa] hover:border-[#D4AF37]'
                }`}
              >
                <div className="mb-4">
                  <h3 className="font-playfair text-2xl font-bold mb-2 text-[#1a1a1a]">{pkg.name}</h3>
                  <div className={`font-mono text-3xl font-bold ${selectedPackage?.id === pkg.id ? 'text-[#1a1a1a]' : 'text-[#D4AF37]'}`}>
                    ₹{pkg.price.toLocaleString('en-IN')}
                    <span className="text-sm font-normal ml-1">+GST</span>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2 font-manrope text-sm">
                    <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      selectedPackage?.id === pkg.id ? 'text-[#1a1a1a]' : 'text-[#D4AF37]'
                    }`} />
                    <span className={selectedPackage?.id === pkg.id ? 'text-[#1a1a1a]' : 'text-[#737373]'}>{pkg.tat_days_min}-{pkg.tat_days_max} days turnaround</span>
                  </li>
                  <li className="flex items-start gap-2 font-manrope text-sm">
                    <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      selectedPackage?.id === pkg.id ? 'text-[#1a1a1a]' : 'text-[#D4AF37]'
                    }`} />
                    <span className={selectedPackage?.id === pkg.id ? 'text-[#1a1a1a]' : 'text-[#737373]'}>{pkg.word_count_min}-{pkg.word_count_max} words</span>
                  </li>
                </ul>
              </button>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
          <div>
            <h2 className="font-playfair text-2xl font-bold mb-4 text-[#1a1a1a]">Editorial Guidelines</h2>
            <p className="font-manrope text-sm text-[#737373]">
              {publication.restrictions}
            </p>
          </div>
          
          <div>
            <h2 className="font-playfair text-2xl font-bold mb-4 text-[#1a1a1a]">Editorial Outcome Policy</h2>
            <p className="font-manrope text-sm text-[#737373]">
              All publications are subject to editorial approval. If rejected, client may choose a replacement outlet of equal value OR a full refund. Writing fee non-refundable once draft delivered.
            </p>
          </div>
        </div>

        {/* Sample Links Section */}
        {publication.sample_links && publication.sample_links.length > 0 && publication.sample_links[0] !== '' && (
          <div className="mb-12">
            <h2 className="font-playfair text-2xl font-bold mb-4 text-[#1a1a1a]">Sample Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {publication.sample_links.filter(link => link && link.trim() !== '').map((link, index) => (
                <a
                  key={index}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 border border-[#e5e5e5] bg-[#fafafa] hover:border-[#D4AF37] transition-all duration-300 group"
                >
                  <ExternalLink className="w-4 h-4 text-[#a3a3a3] group-hover:text-[#D4AF37]" />
                  <span className="font-manrope text-sm text-[#737373] group-hover:text-[#1a1a1a] truncate">
                    Sample Article {index + 1}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex justify-center">
          <button
            onClick={handleBuyNow}
            data-testid="buy-now-button"
            disabled={!selectedPackage}
            className="px-12 py-4 bg-[#D4AF37] text-[#1a1a1a] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
          >
            Buy Now
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PublicationDetail;